import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Calendar, Tag, Share2, Facebook, Twitter, Linkedin,
  ArrowLeft, ArrowRight, Play, Pause, Square, Volume2,
  VolumeX, SkipForward, SkipBack, ChevronDown, ChevronUp
} from 'lucide-react';
import { ArticleService } from './apiService';
import SiteHeader from './components/SiteHeader';
import { translations } from './translations';
import './BlogArticle.css';


const BlogArticle = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [recommendedArticles, setRecommendedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const lang = localStorage.getItem('lang') || 'en';
  const t = (translations[lang] || translations.en).articlePage;
  const locale = lang === 'fr' ? 'fr-FR' : 'en-US';

  // États pour le Text-to-Speech amélioré
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isCompact, setIsCompact] = useState(true);
  const [currentUtterance, setCurrentUtterance] = useState(null);
  const [speechRate, setSpeechRate] = useState(1);
  const [speechVoice, setSpeechVoice] = useState(null);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [sentences, setSentences] = useState([]);
  const [currentText, setCurrentText] = useState('');
  const [progress, setProgress] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  // Charger les voix disponibles
  useEffect(() => {
    const loadVoices = () => {
      const voices = speechSynthesis.getVoices();
      if (voices.length > 0) {
        setAvailableVoices(voices);
        const preferredVoice = voices.find(voice =>
          voice.lang.startsWith('en-') && (voice.name.includes('Female') || voice.name.includes('Natural'))
        ) || voices.find(voice => voice.lang.startsWith('en')) || voices[0];
        setSpeechVoice(preferredVoice);
        setIsInitialized(true);
      }
    };

    loadVoices();
    if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = loadVoices;
    }
    return () => {
      if (speechSynthesis.onvoiceschanged !== undefined) {
        speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  // Préparer le texte pour la lecture
  useEffect(() => {
    if (article?.content) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = article.content;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      const sentenceArray = textContent
        .split(/[.!?]+/)
        .map(s => s.trim())
        .filter(s => s.length > 10);
      setSentences(sentenceArray);
      if (sentenceArray.length > 0) {
        setCurrentText(sentenceArray[0]);
      }
    }
  }, [article]);

  // Fonction de lecture d'une phrase
  const speakSentence = useCallback((sentenceIndex) => {
    if (!sentences[sentenceIndex] || !speechVoice || !isInitialized) return;

    speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(sentences[sentenceIndex]);
    utterance.voice = speechVoice;
    utterance.rate = speechRate;
    utterance.volume = isMuted ? 0 : 1;

    utterance.onstart = () => {
      setCurrentText(sentences[sentenceIndex]);
      setIsPlaying(true);
      setIsPaused(false);
    };

    utterance.onend = () => {
      if (sentenceIndex < sentences.length - 1) {
        setCurrentSentenceIndex(sentenceIndex + 1);
        speakSentence(sentenceIndex + 1);
      } else {
        handleStop();
        showCompletionNotification();
      }
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      handleStop();
    };

    setCurrentUtterance(utterance);
    speechSynthesis.speak(utterance);
  }, [sentences, speechVoice, speechRate, isMuted, isInitialized]);

  // Mise à jour de la progression
  useEffect(() => {
    if (sentences.length > 0) {
      setProgress(((currentSentenceIndex + 1) / sentences.length) * 100);
    }
  }, [currentSentenceIndex, sentences.length]);

  // Gestion des contrôles
  const handlePlayPause = () => {
    if (!isInitialized) {
      alert(t.ttsInitializing);
      return;
    }
    if (isPlaying) {
      if (isPaused) {
        speechSynthesis.resume();
        setIsPaused(false);
      } else {
        speechSynthesis.pause();
        setIsPaused(true);
      }
    } else {
      speakSentence(currentSentenceIndex);
    }
  };

  const handleStop = () => {
    speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentSentenceIndex(0);
    setProgress(0);
    if (sentences.length > 0) setCurrentText(sentences[0]);
  };

  const handleNext = () => {
    if (currentSentenceIndex < sentences.length - 1) {
      const nextIndex = currentSentenceIndex + 1;
      setCurrentSentenceIndex(nextIndex);
      if (isPlaying) speakSentence(nextIndex);
      else setCurrentText(sentences[nextIndex]);
    }
  };

  const handlePrevious = () => {
    if (currentSentenceIndex > 0) {
      const prevIndex = currentSentenceIndex - 1;
      setCurrentSentenceIndex(prevIndex);
      if (isPlaying) speakSentence(prevIndex);
      else setCurrentText(sentences[prevIndex]);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (currentUtterance) {
      speechSynthesis.cancel();
      if (isPlaying && !isPaused) speakSentence(currentSentenceIndex);
    }
  };

  const toggleCompact = () => setIsCompact(!isCompact);

  const showCompletionNotification = () => {
    const notification = document.createElement('div');
    notification.className = 'reading-complete-notification';
    notification.innerHTML = `
      <h3>✓ ${t.readingComplete}</h3>
      <p>${t.readingCompleteMessage}</p>
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.opacity = '0';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  };

  // Charger l'article
  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const articleResponse = await ArticleService.getById(id);
        if (articleResponse.success) {
          setArticle(articleResponse.data);
          try {
            const recommendedResponse = await ArticleService.getRecommended(id, 3);
            if (recommendedResponse.success) {
              setRecommendedArticles(recommendedResponse.data);
            }
          } catch (err) {
            console.error('Erreur lors du chargement des articles recommandés:', err);
          }
        }
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement de l\'article:', error);
        setLoading(false);
      }
    };

    fetchArticle();
    window.scrollTo(0, 0);

    return () => {
      speechSynthesis.cancel();
    };
  }, [id]);

  // Fonctions de partage
  const handleShare = (platform) => {
    const url = window.location.href;
    const title = article?.title || '';
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    alert(t.linkCopied);
  };

  if (loading) {
    return (
      <div className="blog-article-loading">
        <div className="loading-spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="blog-article-not-found">
        <h2>{t.notFound}</h2>
        <p>{t.notFoundMessage}</p>
        <Link to="/news" className="back-to-news">
          <ArrowLeft size={20} />
          {t.backToNews}
        </Link>
      </div>
    );
  }

  return (
    <div className={`blog-article-page ${isCompact ? 'tts-compact' : ''}`}>

      <SiteHeader />

      {/* Navigation */}
      <nav className="blog-nav">
        <div className="container">
          <Link to="/news" className="back-to-news">
            <ArrowLeft size={20} />
            {t.backToNews}
          </Link>
        </div>
      </nav>

      {/* Header de l'article */}
      <header
        className="article-header"
        style={{ backgroundImage: `url(${article.imageArticle || article.image})` }}
      >
        <div className="article-hero-overlay"></div>
        <div className="container">
          <div className="article-header-content">
            <span className="article-category">{article.category?.name || article.category}</span>
            <h1 className="article-title">{article.title}</h1>

            <div className="article-meta">
              <div className="article-meta-item">
                <Calendar size={18} />
                {new Date(article.publish_date || article.date).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>

            {/* Auteur masqué */}
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="article-main">
        <div className="container">
          <div className="article-layout">
            <article className="article-content">
              <div
                className="article-body"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="article-tags">
                <h4>
                  <Tag size={18} />
                  Tags
                </h4>
                <div className="tags-list">
                  {article.tags && article.tags.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag.name || tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Partage */}
              <div className="article-share">
                <h4>
                  <Share2 size={18} />
                  {t.shareArticle}
                </h4>
                <div className="share-buttons">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="share-btn facebook"
                    aria-label={t.shareOnFacebook}
                  >
                    <Facebook size={20} />
                    Facebook
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="share-btn twitter"
                    aria-label={t.shareOnTwitter}
                  >
                    <Twitter size={20} />
                    Twitter
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="share-btn linkedin"
                    aria-label={t.shareOnLinkedIn}
                  >
                    <Linkedin size={20} />
                    LinkedIn
                  </button>
                  <button
                    onClick={copyToClipboard}
                    className="share-btn copy"
                    aria-label={t.copyLink}
                  >
                    <Share2 size={20} />
                    {t.copyLink}
                  </button>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="article-sidebar">
              <div className="sidebar-card author-card">
                {/* Contenu de la carte auteur si nécessaire */}
              </div>

              <div className="sidebar-card related-articles">
                <h3>{t.recommendedArticles}</h3>
                <div className="related-articles-list">
                  {recommendedArticles.map((relatedArticle) => (
                    <Link
                      key={relatedArticle.id}
                      to={`/news/${relatedArticle.id}`}
                      className="related-article"
                    >
                      <img src={relatedArticle.imageArticle || relatedArticle.image} alt={relatedArticle.title} />
                      <div className="related-article-content">
                        <h4>{relatedArticle.title}</h4>
                        <p className="related-article-meta">
                          <Calendar size={14} />
                          {new Date(relatedArticle.publish_date || relatedArticle.date).toLocaleDateString(locale)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Lecteur Text-to-Speech — en bas de la sidebar */}
              {sentences.length > 0 && (
                <div className={`sidebar-card tts-sidebar ${isCompact ? 'compact' : ''}`}>
                  <div className="tts-container">
                    <div className="tts-controls">
                      <div className="tts-main-controls">
                        <button onClick={handlePrevious} className="tts-btn" disabled={currentSentenceIndex === 0 || !isInitialized} aria-label="Phrase précédente" title="Phrase précédente">
                          <SkipBack size={20} />
                        </button>
                        <button onClick={handlePlayPause} className="tts-btn primary" disabled={!isInitialized} aria-label={isPlaying ? (isPaused ? 'Reprendre' : 'Pause') : 'Lecture'} title={isPlaying ? (isPaused ? 'Reprendre' : 'Pause') : 'Lecture'}>
                          {isPlaying && !isPaused ? <Pause size={24} /> : <Play size={24} />}
                        </button>
                        <button onClick={handleStop} className="tts-btn" disabled={!isPlaying || !isInitialized} aria-label="Arrêter" title="Arrêter">
                          <Square size={20} />
                        </button>
                        <button onClick={handleNext} className="tts-btn" disabled={currentSentenceIndex >= sentences.length - 1 || !isInitialized} aria-label="Phrase suivante" title="Phrase suivante">
                          <SkipForward size={20} />
                        </button>
                        <button onClick={toggleMute} className="tts-btn" disabled={!isInitialized} aria-label={isMuted ? 'Activer le son' : 'Couper le son'} title={isMuted ? 'Activer le son' : 'Couper le son'}>
                          {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                        </button>
                      </div>

                      {!isCompact && (
                        <div className="tts-info">
                          <div className="tts-current-text reading">
                            <Volume2 size={18} className="volume-icon" />
                            <span>{currentText || t.ttsReady}</span>
                          </div>
                          <div className="tts-progress-info">
                            <div className="tts-progress-counter">
                              <span>{currentSentenceIndex + 1}</span>
                              <span className="tts-separator">/</span>
                              <span>{sentences.length}</span>
                              <span style={{ marginLeft: '0.5rem', opacity: 0.8 }}>{t.ttsSentences}</span>
                            </div>
                            <div className="tts-progress-bar" style={{ flex: 1, marginLeft: '1rem' }}>
                              <div className="tts-progress-fill" style={{ width: `${progress}%` }} />
                            </div>
                          </div>
                        </div>
                      )}

                      {!isCompact && (
                        <div className="tts-settings">
                          <div className="tts-setting">
                            <label htmlFor="voice-select">{t.ttsVoice}</label>
                            <select id="voice-select" value={speechVoice?.name || ''} onChange={(e) => { const voice = availableVoices.find(v => v.name === e.target.value); setSpeechVoice(voice); }} className="tts-select" disabled={!isInitialized}>
                              {availableVoices.map((voice) => (
                                <option key={voice.name} value={voice.name}>{voice.name.substring(0, 20)}</option>
                              ))}
                            </select>
                          </div>
                          <div className="tts-setting">
                            <label htmlFor="rate-select">{t.ttsSpeed}</label>
                            <select id="rate-select" value={speechRate} onChange={(e) => setSpeechRate(parseFloat(e.target.value))} className="tts-select" disabled={!isInitialized}>
                              <option value="0.5">0.5x</option>
                              <option value="0.75">0.75x</option>
                              <option value="1">1x</option>
                              <option value="1.25">1.25x</option>
                              <option value="1.5">1.5x</option>
                              <option value="2">2x</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <button onClick={toggleCompact} className="tts-toggle-btn" aria-label={isCompact ? 'Agrandir le lecteur' : 'Réduire le lecteur'} title={isCompact ? 'Agrandir le lecteur' : 'Réduire le lecteur'}>
                    {isCompact ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                  </button>
                </div>
              )}
            </aside>
          </div>
        </div>
      </main>

      {/* Navigation articles */}
      <section className="article-navigation">
        <div className="container">
          <div className="nav-articles">
            {parseInt(id) > 1 && (
              <Link to={`/news/${parseInt(id) - 1}`} className="nav-article prev">
                <ArrowLeft size={20} />
                <div>
                  <span>{t.previousArticle}</span>
                  <h4>{t.previousArticle}</h4>
                </div>
              </Link>
            )}

            <Link to={`/news/${parseInt(id) + 1}`} className="nav-article next">
              <div>
                <span>{t.nextArticle}</span>
                <h4>{t.nextArticle}</h4>
              </div>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogArticle;
